/**
 * SizerAddressInput — the property-address picker for a PLAIN-STATE sizer form.
 *
 * `RffAddressEditingFieldGoogle` is the portal's standard control and the RTL
 * sizer uses it directly, but it is bound to react-final-form (`useForm`,
 * `<Field>`), and the DSCR sizer's form is plain `useState`. So this is the same
 * Places v1 autocomplete and the same `Address` mapping, driven by
 * `value` / `onChange` instead of a form name, and restyled through
 * `SizerMuiField` so it sits at the same height as every other control on the
 * card.
 *
 * WHY THE SIZER ASKS FOR AN ADDRESS AT ALL: `POST /pricing/scenario/dscr_quick`
 * now gates on `address.zip` (422) — market data and the CFPB rural
 * determination embed into the price. A FULL address resolves rural at ADDRESS
 * level (HouseCanary census / census.gov FIPS); a bare ZIP falls back to the
 * county crosswalk. So the picker is the ask and the ZIP box beside it is the
 * floor, not a peer (pricing-scenario-sizers.md §2).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";

import Txt from "../../components/typography/Txt";
import { SZ, SizerMuiField } from "./sizerUi";

const PLACES_AUTOCOMPLETE_URL =
  "https://places.googleapis.com/v1/places:autocomplete";
const PLACES_DETAILS_URL = "https://places.googleapis.com/v1/places";

const component = (components, type) =>
  components?.find((c) => c.types?.includes(type));
const short = (components, type) => component(components, type)?.shortText || "";
const long = (components, type) => component(components, type)?.longText || "";

/**
 * Google Place details -> the portal's canonical `Address` object.
 *
 * The SAME shape and the SAME component mapping as
 * `RffAddressEditingFieldGoogle` (fullAddress / streetNumber / streetName /
 * city / state / zip / county / lat / lng), because a sizer address is posted to
 * the pricing endpoint and then carries onto the saved deal — a thinner address
 * here would be a thinner address on the loan.
 *
 * `latitude` / `longitude` are not decoration: `cfpb_rural_check` reuses the
 * frontend's geocode directly for the TIGER county-polygon lookup rather than
 * re-geocoding, so dropping them costs the rural determination a signal.
 */
export const placeToAddress = (place, suggestionId) => {
  const components = place?.addressComponents || [];
  const lat = place?.location?.latitude || 0;
  const lng = place?.location?.longitude || 0;
  return {
    id: `${place?.id || suggestionId}-${Date.now()}`,
    fullAddress: place?.formattedAddress || "",
    streetNumber: short(components, "street_number"),
    streetName: long(components, "route"),
    zip: short(components, "postal_code"),
    county: long(components, "administrative_area_level_2"),
    state: short(components, "administrative_area_level_1"),
    city:
      long(components, "locality") ||
      long(components, "sublocality") ||
      long(components, "postal_town"),
    country: long(components, "country"),
    countryCode: short(components, "country"),
    latitude: lat,
    longitude: lng,
    coordinateTuple: [lat, lng],
  };
};

/**
 * `SizerMuiField` normalizes a portal MUI control onto the sizer look, but it
 * sizes to 38px — and this control sits in a row beside the kit's PLAIN inputs,
 * which are 42px. A 4px mismatch across an address row is exactly the drift the
 * RTL sizer pins `FIELD_H` to avoid, so pin it here for the same reason.
 */
const FIELD_H = 42;
const FIELD_SX = {
  ".MuiOutlinedInput-root": {
    height: FIELD_H,
    minHeight: FIELD_H,
    py: 0,
    // The Autocomplete keeps room for its clear/loading adornment; without this
    // the text sits hard against it once an address is picked.
    pr: "38px !important",
  },
  ".MuiAutocomplete-input": { py: "0 !important" },
};

export default function SizerAddressInput({
  value,
  onChange,
  placeholder,
  disabled,
}) {
  const [options, setOptions] = useState([]);
  const [typed, setTyped] = useState(null);
  const [loading, setLoading] = useState(false);
  const googleMapsKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  const search = useCallback(
    async (query) => {
      if (!query || query.length < 3) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(PLACES_AUTOCOMPLETE_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": googleMapsKey,
          },
          body: JSON.stringify({
            input: query,
            includedRegionCodes: ["us"],
            includedPrimaryTypes: [
              "street_address",
              "premise",
              "subpremise",
              "route",
            ],
          }),
        });
        const data = await res.json();
        setOptions(
          (data.suggestions || [])
            .map((s) => s.placePrediction)
            .filter(Boolean)
            .map((p) => ({
              placeId: p.placeId,
              fullAddress: p.text?.text || "",
              mainText: p.structuredFormat?.mainText?.text || "",
              secondaryText: p.structuredFormat?.secondaryText?.text || "",
            })),
        );
      } catch (err) {
        console.error("Sizer address search error:", err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [googleMapsKey],
  );

  const debounced = useMemo(() => debounce(search, 400), [search]);
  useEffect(() => {
    if (typed != null) debounced(typed);
    return () => debounced.cancel();
  }, [typed, debounced]);

  const pick = async (option) => {
    if (!option?.placeId) return;
    try {
      const res = await fetch(`${PLACES_DETAILS_URL}/${option.placeId}`, {
        method: "GET",
        headers: {
          "X-Goog-Api-Key": googleMapsKey,
          "X-Goog-FieldMask": "id,formattedAddress,addressComponents,location",
        },
      });
      onChange?.(placeToAddress(await res.json(), option.placeId));
    } catch (err) {
      console.error("Sizer address details error:", err);
      // Keep what the user meant. The ZIP box beside this one is what actually
      // gates the price, so a failed details lookup is not a dead end.
      onChange?.({ ...(value || {}), fullAddress: option.fullAddress });
    }
  };

  return (
    <SizerMuiField sx={FIELD_SX}>
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={options}
        // Server-side matching — never re-filter Places results locally.
        filterOptions={(o) => o}
        loading={loading}
        inputValue={typed != null ? typed : value?.fullAddress || ""}
        onInputChange={(e, next, reason) => {
          if (reason === "input") setTyped(next);
          if (reason === "clear") {
            setTyped("");
            onChange?.({});
          }
        }}
        onChange={(e, option) => {
          if (option && typeof option === "object") {
            setTyped(null);
            pick(option);
          }
        }}
        getOptionLabel={(o) => (typeof o === "string" ? o : o.fullAddress || "")}
        isOptionEqualToValue={(o, v) => o.placeId === v?.placeId}
        noOptionsText="Type at least 3 characters"
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.placeId}>
            <Box sx={{ minWidth: 0 }}>
              <Txt component="div" sx={{ fontSize: 13, color: SZ.ink900 }}>
                {option.mainText || option.fullAddress}
              </Txt>
              {option.secondaryText && (
                <Txt component="div" sx={{ fontSize: 11.5, color: SZ.ink400 }}>
                  {option.secondaryText}
                </Txt>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || "Start typing the property address"}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress size={15} sx={{ color: SZ.teal600 }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </SizerMuiField>
  );
}
